import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaCar, FaSearch, FaPlus, FaSave, FaTimes, FaEdit, FaTrash, FaCheck,
  FaUser, FaFileAlt, FaWrench, FaBoxOpen, FaBox, FaBoxes, FaShapes, FaLayerGroup,
  FaMoneyBillWave, FaArrowLeft, FaClipboardList, FaOilCan,
  FaCarBattery, FaFan, FaGasPump, FaCarCrash
} from 'react-icons/fa';
import Header from '../others/Header';
import config from '../config/config';
import InputMask from 'react-input-mask';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../components/ToastContext';
import ConfirmModal from '../components/ConfirmModal';
import ModalProcessoDaOrdem from '../components/ModalProcessoDaOrdem';
import ImportarDocumento from '../components/ImportarDocumento';
import { resolverAnexo } from '../others/importacao';
import { anexarDocumentoVeiculo } from '../others/anexos';

const gerarIdTemp = () => `item_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// ══════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════
function formatarMoeda(valor) {
  if (valor === null || valor === undefined) return '0,00';
  const numero = typeof valor === 'string' ? parseFloat(valor) : valor;
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numero || 0);
}

function normalizePlaca(value) {
  const raw = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (raw.length <= 3) return raw;
  const parte1 = raw.slice(0, 3);
  const parte2 = raw.slice(3, 7);
  return `${parte1}-${parte2}`.slice(0, 8);
}

function getServicoIcon(descricao) {
  const lowerDesc = descricao?.toLowerCase() || '';
  if (lowerDesc.includes('óleo')) return <FaOilCan />;
  if (lowerDesc.includes('bateria')) return <FaCarBattery />;
  if (lowerDesc.includes('radiador')) return <FaFan />;
  if (lowerDesc.includes('combustível')) return <FaGasPump />;
  if (lowerDesc.includes('colisão')) return <FaCarCrash />;
  return <FaWrench />;
}

function getServicoPassivoIcon(descricao) {
  const lowerDesc = descricao?.toLowerCase() || '';
  if (lowerDesc.includes('peça')) return <FaBox />;
  if (lowerDesc.includes('material')) return <FaBoxes />;
  if (lowerDesc.includes('componente')) return <FaShapes />;
  if (lowerDesc.includes('kit')) return <FaLayerGroup />;
  return <FaBoxOpen />;
}

// ══════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════
function OrdemServico() {
  const { API_BASE_URL } = config;
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(false);
  // ── NOVO: depois de gerar a OS com veículo, pergunta se abre o processo ({ ordem, quantidadeVeiculos })
  const [processoDaOrdem, setProcessoDaOrdem] = useState(null);
  const [idOrdemServico, setIdOrdemServico] = useState(null);
  const [observacoes, setObservacoes] = useState('');

  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  // ── NOVO: bifurcação do fluxo — 'VEICULAR' | 'NAO_VEICULAR'
  const [tipoOrdem, setTipoOrdem] = useState(null);

  const [servicosDisponiveis, setServicosDisponiveis] = useState([]);
  const [buscaProduto, setBuscaProduto] = useState('');

  // ── NOVO: lista plana de itens selecionados (cada um pode carregar seu próprio veículo)
  const [itensSelecionados, setItensSelecionados] = useState([]);

  const [modalClienteAberto, setModalClienteAberto] = useState(false);
  const [termoPesquisaCliente, setTermoPesquisaCliente] = useState('');
  const [resultadosPesquisaCliente, setResultadosPesquisaCliente] = useState([]);
  const [buscandoCliente, setBuscandoCliente] = useState(false);

  const [modalTipoOrdemAberto, setModalTipoOrdemAberto] = useState(false);

  const [modalVeiculoItem, setModalVeiculoItem] = useState({ open: false, itemId: null });
  const [placaPesquisaItem, setPlacaPesquisaItem] = useState('');
  const [loadingPlaca, setLoadingPlaca] = useState(false);
  // ── NOVO: alternativa ao veículo cadastrado — informar placa/chassi manualmente (ex: ATPV-e sem cadastro prévio)
  const [modoVinculoVeiculo, setModoVinculoVeiculo] = useState('buscar'); // 'buscar' | 'manual'
  const [placaManualInput, setPlacaManualInput] = useState('');
  const [chassiManualInput, setChassiManualInput] = useState('');

  const [modalCadastroVeiculo, setModalCadastroVeiculo] = useState({ open: false, itemId: null });
  const [novoVeiculo, setNovoVeiculo] = useState({ placa: '', marcaModelo: '', cor: '', ano: '', renavam: '' });
  const [importacaoNovoVeiculo, setImportacaoNovoVeiculo] = useState(null); // CRLV/ATPV importado (a ser anexado ao veículo ao cadastrar)

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false, message: '', onConfirm: () => {}, title: 'Confirmação'
  });
  const showConfirm = (message, onConfirm, title = 'Confirmação') =>
    setConfirmModal({ isOpen: true, message, onConfirm, title });
  const closeConfirm = () =>
    setConfirmModal({ isOpen: false, message: '', onConfirm: () => {}, title: 'Confirmação' });

  const getAuthHeader = () => {
    const token = localStorage.getItem('jwtToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // ── Carrega catálogo de serviços (mesmo endpoint de sempre)
  useEffect(() => {
    const carregarServicos = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/servicos`, getAuthHeader());
        setServicosDisponiveis(response.data || []);
      } catch (err) {
        console.error('Erro ao carregar serviços:', err);
      }
    };
    carregarServicos();
  }, []);

  // ── Carrega ordem existente (modo edição)
  useEffect(() => {
    if (id) carregarOrdemExistente(id);
  }, [id]);

  // ── Restaura rascunho salvo (quando o usuário foi cadastrar cliente/veículo no meio do processo)
  useEffect(() => {
    if (id) return;
    const rascunho = localStorage.getItem('ordemServicoRascunho');
    if (!rascunho) return;
    try {
      const estado = JSON.parse(rascunho);
      setClienteSelecionado(estado.cliente || null);
      setTipoOrdem(estado.tipoOrdem || null);
      setItensSelecionados(estado.itensSelecionados || []);
      setObservacoes(estado.observacoes || '');
    } catch (err) {
      console.error('Erro ao restaurar rascunho da ordem:', err);
    } finally {
      localStorage.removeItem('ordemServicoRascunho');
    }
  }, [id]);

  const salvarRascunhoENavegar = (destino, dadosExtras = {}) => {
    const rascunho = { cliente: clienteSelecionado, tipoOrdem, itensSelecionados, observacoes };
    localStorage.setItem('ordemServicoRascunho', JSON.stringify(rascunho));
    localStorage.setItem('redirectAfterClientCreation', window.location.pathname);
    navigate(destino, { state: dadosExtras });
  };

  // ══════════════════════════════════════════════════════════════
  // BUSCA DE CLIENTE (ao vivo)
  // ══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!modalClienteAberto) return;
    if (termoPesquisaCliente.trim().length < 3) {
      setResultadosPesquisaCliente([]);
      return;
    }
    const timer = setTimeout(() => {
      executarPesquisaCliente(termoPesquisaCliente.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [termoPesquisaCliente, modalClienteAberto]);

  const executarPesquisaCliente = async (termo) => {
    setBuscandoCliente(true);
    setResultadosPesquisaCliente([]);
    // ── CORREÇÃO: codifica o termo (nomes com espaço quebravam a URL) e busca por nome em minúsculas,
    // igual ao Clientes.jsx (o back parece comparar nome de forma case-sensitive)
    const termoCodificado = encodeURIComponent(termo);
    try {
      try {
        const responseCpf = await axios.get(`${API_BASE_URL}/clientes/cpfcnpj/${termoCodificado}`, getAuthHeader());
        if (responseCpf.data) {
          setResultadosPesquisaCliente([responseCpf.data]);
          return;
        }
      } catch (e) {
        // tenta por nome
      }
      const responseNome = await axios.get(
        `${API_BASE_URL}/clientes/nome/${encodeURIComponent(termo.toLowerCase())}`,
        getAuthHeader()
      );
      setResultadosPesquisaCliente(responseNome.data && responseNome.data.length > 0 ? responseNome.data : []);
    } catch (err) {
      console.error('Erro na pesquisa de cliente:', err);
    } finally {
      setBuscandoCliente(false);
    }
  };

  const selecionarCliente = async (cliente) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/clientes/${cliente.idCliente}`, getAuthHeader());
      const clienteTrocado = clienteSelecionado && clienteSelecionado.idCliente !== response.data.idCliente;

      const aplicar = () => {
        setClienteSelecionado(response.data);
        setModalClienteAberto(false);
        setTermoPesquisaCliente('');
        setResultadosPesquisaCliente([]);
        if (!tipoOrdem) setModalTipoOrdemAberto(true);
      };

      if (clienteTrocado && itensSelecionados.length > 0) {
        showConfirm(
          'Trocar o cliente vai limpar os serviços já selecionados nesta ordem. Deseja continuar?',
          () => {
            setItensSelecionados([]);
            aplicar();
          },
          'Trocar cliente'
        );
      } else {
        aplicar();
      }
    } catch (err) {
      console.error('Erro ao carregar cliente:', err);
      addToast('error', 'Erro ao carregar dados do cliente');
    } finally {
      setLoading(false);
    }
  };

  // ── NOVO: editar cliente direto a partir do resultado da busca
  const editarClienteEncontrado = (cliente) => {
    salvarRascunhoENavegar('/Clientes', { clienteParaEditar: cliente });
  };

  const solicitarCadastroCliente = () => {
    showConfirm(
      'Cliente não encontrado. Deseja cadastrar um novo cliente?',
      () => {
        localStorage.setItem('clienteSearchTerm', termoPesquisaCliente);
        salvarRascunhoENavegar('/Clientes', {});
      },
      'Cadastrar novo cliente'
    );
  };

  // ══════════════════════════════════════════════════════════════
  // TIPO DE ORDEM (veicular / não-veicular)
  // ══════════════════════════════════════════════════════════════
  const escolherTipoOrdem = (tipo) => {
    if (tipoOrdem && tipoOrdem !== tipo && itensSelecionados.length > 0) {
      showConfirm(
        'Alterar o tipo de ordem vai limpar os serviços já selecionados. Deseja continuar?',
        () => {
          setItensSelecionados([]);
          setTipoOrdem(tipo);
          setModalTipoOrdemAberto(false);
        },
        'Alterar tipo de ordem'
      );
    } else {
      setTipoOrdem(tipo);
      setModalTipoOrdemAberto(false);
    }
  };

  // ══════════════════════════════════════════════════════════════
  // CATÁLOGO / ITENS SELECIONADOS
  // ══════════════════════════════════════════════════════════════
  const servicosFiltrados = servicosDisponiveis
    .filter(s => (tipoOrdem === 'VEICULAR' ? !s.naoRelacionadoVeiculo : !!s.naoRelacionadoVeiculo))
    // ── Serviços inativos ou "aguardando cadastro" (sem valor definido) não podem ser usados numa OS
    .filter(s => s.ativo !== false)
    .filter(s => s.valor != null)
    .filter(s => s.descricao?.toLowerCase().includes(buscaProduto.toLowerCase()));

  const adicionarItemServico = (servico) => {
    const novoItem = {
      itemId: gerarIdTemp(),
      servico: { idServico: servico.idServico, descricao: servico.descricao, valor: servico.valor || 0 },
      veiculo: null,
      placaManual: '',
      chassiManual: '',
      // ── Quantidade só é usada no fluxo sem veículo (repete o mesmo serviço N vezes ao salvar)
      quantidade: 1,
      passivos: (servico.servicoPassivos || []).map(p => ({
        idServicoPassivo: p.idServicoPassivo,
        descricao: p.descricao,
        valor: p.valorDigitavel ? 0 : (p.valor || 0),
        valorDigitavel: !!p.valorDigitavel,
        selecionado: false
      }))
    };
    setItensSelecionados(prev => [...prev, novoItem]);
  };

  const removerItem = (itemId) => {
    setItensSelecionados(prev => prev.filter(i => i.itemId !== itemId));
  };

  const alterarQuantidadeItem = (itemId, delta) => {
    setItensSelecionados(prev => prev.map(item => {
      if (item.itemId !== itemId) return item;
      const novaQuantidade = Math.max(1, (item.quantidade || 1) + delta);
      return { ...item, quantidade: novaQuantidade };
    }));
  };

  const togglePassivo = (itemId, idServicoPassivo, checked) => {
    setItensSelecionados(prev => prev.map(item => {
      if (item.itemId !== itemId) return item;
      return {
        ...item,
        passivos: item.passivos.map(p =>
          p.idServicoPassivo === idServicoPassivo ? { ...p, selecionado: checked } : p
        )
      };
    }));
  };

  const alterarValorPassivo = (itemId, idServicoPassivo, valor) => {
    setItensSelecionados(prev => prev.map(item => {
      if (item.itemId !== itemId) return item;
      return {
        ...item,
        passivos: item.passivos.map(p =>
          p.idServicoPassivo === idServicoPassivo ? { ...p, valor } : p
        )
      };
    }));
  };

  const calcularSubtotalItem = (item) => {
    const somaPassivos = item.passivos?.filter(p => p.selecionado).reduce((sum, p) => sum + (p.valor || 0), 0) || 0;
    return ((item.servico.valor || 0) + somaPassivos) * (item.quantidade || 1);
  };

  const valorTotal = itensSelecionados.reduce((total, item) => total + calcularSubtotalItem(item), 0);

  // ══════════════════════════════════════════════════════════════
  // VÍNCULO DE VEÍCULO POR ITEM
  // ══════════════════════════════════════════════════════════════
  const abrirModalVeiculo = (itemId) => {
    setPlacaPesquisaItem('');
    setModoVinculoVeiculo('buscar');
    setPlacaManualInput('');
    setChassiManualInput('');
    setModalVeiculoItem({ open: true, itemId });
  };

  const vincularVeiculoItem = (itemId, veiculo) => {
    // ── Veículo cadastrado e identificação manual são mutuamente exclusivas
    setItensSelecionados(prev => prev.map(item => (
      item.itemId === itemId ? { ...item, veiculo, placaManual: '', chassiManual: '' } : item
    )));
    setModalVeiculoItem({ open: false, itemId: null });
  };

  const vincularIdentificacaoManual = (itemId) => {
    if (!placaManualInput.trim() && !chassiManualInput.trim()) return;
    setItensSelecionados(prev => prev.map(item => (
      item.itemId === itemId
        ? { ...item, veiculo: null, placaManual: placaManualInput.trim(), chassiManual: chassiManualInput.trim() }
        : item
    )));
    setModalVeiculoItem({ open: false, itemId: null });
  };

  const handlePlacaChangeItem = (e) => {
    setPlacaPesquisaItem(normalizePlaca(e.target.value));
  };

  const pesquisarPlacaItem = async () => {
    if (!placaPesquisaItem.trim()) return;
    setLoadingPlaca(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/veiculos/placa/${placaPesquisaItem}`, getAuthHeader());
      if (response.data) {
        vincularVeiculoItem(modalVeiculoItem.itemId, response.data);
        setPlacaPesquisaItem('');
      } else {
        abrirCadastroVeiculo(modalVeiculoItem.itemId, placaPesquisaItem);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        abrirCadastroVeiculo(modalVeiculoItem.itemId, placaPesquisaItem);
      } else {
        addToast('error', 'Erro ao pesquisar placa: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setLoadingPlaca(false);
    }
  };

  const abrirCadastroVeiculo = (itemId, placa) => {
    setNovoVeiculo({ placa, marcaModelo: '', cor: '', ano: '', renavam: '' });
    setImportacaoNovoVeiculo(null);
    setModalCadastroVeiculo({ open: true, itemId });
    setModalVeiculoItem({ open: false, itemId: null });
  };

  // Recebe placa, RENAVAM, marca/modelo, cor e ano lidos do CRLV/ATPV (este formulário não tem chassi)
  const aplicarDadosVeiculoImportado = (dados) => {
    setNovoVeiculo((prev) => ({ ...prev, ...dados, ...(dados.placa ? { placa: normalizePlaca(dados.placa) } : {}) }));
  };

  const cadastrarNovoVeiculo = async () => {
    try {
      // ── CORREÇÃO: agora que o cadastro acontece direto por aqui (sem passar pela tela de Clientes),
      // é preciso vincular o veículo ao cliente no payload, igual ao Clientes.jsx (salvarVeiculo)
      const veiculoData = { ...novoVeiculo, cliente: { idCliente: clienteSelecionado.idCliente } };
      const response = await axios.post(`${API_BASE_URL}/veiculos`, veiculoData, getAuthHeader());
      // Atualiza a lista de veículos do cliente em memória para já aparecer nos próximos itens
      setClienteSelecionado(prev => ({ ...prev, veiculos: [...(prev.veiculos || []), response.data] }));
      vincularVeiculoItem(modalCadastroVeiculo.itemId, response.data);
      setModalCadastroVeiculo({ open: false, itemId: null });
      addToast('success', 'Veículo cadastrado e vinculado com sucesso!');
      const { arquivo: arquivoVeiculo, divergente } = resolverAnexo(importacaoNovoVeiculo, response.data.placa);
      setImportacaoNovoVeiculo(null);
      if (divergente) addToast('warning', 'O documento importado é de outra placa; não foi anexado a este veículo. Anexe-o pelos documentos do veículo, se for o caso.');
      if (arquivoVeiculo && !(await anexarDocumentoVeiculo(API_BASE_URL, response.data.idVeiculo, arquivoVeiculo, getAuthHeader()))) {
        addToast('warning', 'Veículo salvo, mas não foi possível guardar o documento. Anexe-o pelos documentos do veículo.');
      }
    } catch (err) {
      addToast('error', 'Erro ao cadastrar veículo: ' + (err.response?.data?.message || err.message));
    }
  };

  // ══════════════════════════════════════════════════════════════
  // CARREGAR / SALVAR ORDEM
  // ══════════════════════════════════════════════════════════════
  const carregarOrdemExistente = async (idOrdem) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/ordemservico/${idOrdem}`, getAuthHeader());
      const ordem = response.data;

      setIdOrdemServico(ordem.idOrdemServico);
      setObservacoes(ordem.observacoes || '');
      setClienteSelecionado(ordem.cliente);

      const itens = [];
      let algumComVeiculo = false;
      (ordem.ordemVeiculosDTO || []).forEach(ov => {
        const veiculo = ov.veiculo || null;
        const placaManual = ov.placaManual || '';
        const chassiManual = ov.chassiManual || '';
        // ── Placa/chassi manual também identifica um item como veicular, não só veículo cadastrado
        if (veiculo || placaManual || chassiManual) algumComVeiculo = true;
        (ov.veiculosServico || []).forEach(vs => {
          itens.push({
            itemId: gerarIdTemp(),
            servico: {
              idServico: vs.servico.idServico,
              descricao: vs.servico.descricao,
              valor: vs.servico.valor
            },
            veiculo,
            placaManual,
            chassiManual,
            passivos: (vs.servicosPassivosLig || []).map(spl => ({
              idServicoPassivo: spl.servicoPassivo.idServicoPassivo,
              descricao: spl.servicoPassivo.descricao,
              valor: spl.valor,
              valorDigitavel: spl.servicoPassivo.valorDigitavel,
              selecionado: true
            }))
          });
        });
      });

      setItensSelecionados(itens);
      setTipoOrdem(algumComVeiculo ? 'VEICULAR' : 'NAO_VEICULAR');
    } catch (err) {
      console.error('Erro ao carregar ordem:', err);
      addToast('error', 'Erro ao carregar ordem de serviço');
      navigate('/ordemServico');
    } finally {
      setLoading(false);
    }
  };

  const validarOrdem = () => {
    if (!clienteSelecionado) {
      addToast('warning', 'Selecione um cliente');
      return false;
    }
    if (!tipoOrdem) {
      addToast('warning', 'Selecione o tipo de atendimento');
      return false;
    }
    if (itensSelecionados.length === 0) {
      addToast('warning', 'Adicione ao menos um serviço');
      return false;
    }
    if (tipoOrdem === 'VEICULAR' && itensSelecionados.some(i => !i.veiculo && !i.placaManual?.trim() && !i.chassiManual?.trim())) {
      addToast('warning', 'Informe um veículo (cadastrado, placa ou chassi) para todos os serviços da ordem');
      return false;
    }
    return true;
  };

  // ── Monta o payload no MESMO formato já usado pelo back (ordensVeiculos: [{veiculo, veiculosServico}])
  // Itens sem veículo (fluxo não-veicular) entram agrupados com veiculo: null.
  // Itens com placa/chassi manual (sem cadastro prévio) agrupam pelo valor digitado, não por idVeiculo.
  const prepararDadosOrdem = () => {
    const grupos = {};
    itensSelecionados.forEach(item => {
      const placaManual = item.placaManual?.trim();
      const chassiManual = item.chassiManual?.trim();
      const chave = item.veiculo?.idVeiculo
        ? `v_${item.veiculo.idVeiculo}`
        : placaManual
          ? `p_${placaManual.toUpperCase()}`
          : chassiManual
            ? `c_${chassiManual.toUpperCase()}`
            : '__sem_veiculo__';
      if (!grupos[chave]) {
        grupos[chave] = { veiculo: item.veiculo || null, placaManual: placaManual || null, chassiManual: chassiManual || null, itens: [] };
      }
      grupos[chave].itens.push(item);
    });

    const ordensVeiculos = Object.values(grupos).map(grupo => ({
      veiculo: grupo.veiculo ? { idVeiculo: grupo.veiculo.idVeiculo } : null,
      placaManual: grupo.placaManual,
      chassiManual: grupo.chassiManual,
      // ── O back não conhece "quantidade": cada unidade vira sua própria linha de veiculosServico
      veiculosServico: grupo.itens.flatMap(item => {
        const linha = {
          servico: { idServico: item.servico.idServico },
          valor: item.servico.valor,
          servicosPassivosLig: item.passivos
            ?.filter(p => p.selecionado)
            .map(p => ({
              servicoPassivo: { idServicoPassivo: p.idServicoPassivo },
              valor: p.valor
            })) || []
        };
        return Array(item.quantidade || 1).fill(linha);
      })
    }));

    return {
      cliente: { idCliente: clienteSelecionado.idCliente },
      descricao: 'Ordem de serviço',
      observacoes: observacoes || '',
      ordensVeiculos
    };
  };

  const salvarOrdemServico = async () => {
    if (!validarOrdem()) return;
    setLoading(true);
    try {
      const dadosOrdem = prepararDadosOrdem();
      if (idOrdemServico) {
        await axios.put(`${API_BASE_URL}/ordemservico/${idOrdemServico}`, dadosOrdem, getAuthHeader());
        addToast('success', 'Ordem atualizada com sucesso!');
      } else {
        const { data: ordemCriada } = await axios.post(`${API_BASE_URL}/ordemservico`, dadosOrdem, getAuthHeader());
        addToast('success', 'Ordem criada com sucesso!');
        // ── NOVO: OS com veículo oferece abrir o processo (um por veículo); sem veículo segue como sempre
        const comVeiculo = dadosOrdem.ordensVeiculos.filter((ov) => ov.veiculo || ov.placaManual || ov.chassiManual).length;
        if (comVeiculo > 0 && ordemCriada?.id) {
          localStorage.removeItem('ordemServicoRascunho');
          setProcessoDaOrdem({ ordem: ordemCriada, quantidadeVeiculos: comVeiculo });
          return;
        }
      }
      localStorage.removeItem('ordemServicoRascunho');
      navigate('/ordemServico');
    } catch (err) {
      console.error('Erro ao salvar ordem:', err.response?.data || err.message);
      addToast('error', `Erro ao salvar ordem: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const limparOrdem = () => {
    setIdOrdemServico(null);
    setObservacoes('');
    setClienteSelecionado(null);
    setTipoOrdem(null);
    setItensSelecionados([]);
    localStorage.removeItem('ordemServicoRascunho');
  };

  // ══════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════
  return (
    <div style={styles.container}>
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Confirmar"
        cancelText="Cancelar"
      />
      <Header />
      <div style={styles.content}>
        <div style={styles.header}>
          <FaClipboardList style={styles.titleIcon} />
          <div>
            <h1 style={styles.title}>{idOrdemServico ? `OS #${idOrdemServico}` : 'Nova Ordem de Serviço'}</h1>
            <p style={styles.subtitle}>Selecione o cliente e adicione os serviços</p>
          </div>
        </div>

        {/* ══════════ PAINEL: CLIENTE ══════════ */}
        <div style={styles.painel}>
          <h3 style={styles.painelTitulo}><FaUser style={{ marginRight: 8 }} /> Cliente</h3>
          {!clienteSelecionado ? (
            <button onClick={() => setModalClienteAberto(true)} style={styles.tealButton}>
              <FaSearch /> Buscar Cliente
            </button>
          ) : (
            <div style={styles.clienteBox}>
              <div style={styles.clienteAvatar}>{clienteSelecionado.nome?.charAt(0) || '?'}</div>
              <div style={{ flex: 1 }}>
                <div style={styles.clienteNome}>{clienteSelecionado.nome}</div>
                <div style={styles.clienteSub}>{clienteSelecionado.cpfCnpj}</div>
              </div>
              <button onClick={() => setModalClienteAberto(true)} style={styles.tealOutlineButton}>
                <FaEdit /> Alterar
              </button>
            </div>
          )}
        </div>

        {/* ══════════ BARRA: TIPO DE ORDEM ══════════ */}
        {clienteSelecionado && tipoOrdem && (
          <div style={styles.tipoOrdemBar}>
            <span style={styles.tipoOrdemLabel}>
              {tipoOrdem === 'VEICULAR' ? (
                <><FaCar style={{ marginRight: 8 }} /> Ordem relacionada a veículo</>
              ) : (
                <><FaFileAlt style={{ marginRight: 8 }} /> Ordem sem veículo</>
              )}
            </span>
            <button onClick={() => setModalTipoOrdemAberto(true)} style={styles.tipoOrdemAlterar}>
              Alterar
            </button>
          </div>
        )}

        {/* ══════════ COLUNAS: SELECIONADOS x DISPONÍVEIS ══════════ */}
        {clienteSelecionado && tipoOrdem && (
          <>
            <div style={styles.colunas}>
              {/* Selecionados */}
              <div style={styles.painel}>
                <h3 style={styles.painelTitulo}>
                  <FaCheck style={{ marginRight: 8, color: '#16a34a' }} />
                  Selecionados ({itensSelecionados.length})
                </h3>
                {itensSelecionados.length === 0 ? (
                  <div style={styles.emptyState}>
                    <FaBoxOpen style={styles.emptyStateIcon} />
                    <span>Nenhum produto selecionado</span>
                    <span style={styles.emptyStateHint}>Clique duas vezes nos itens ao lado para adicionar</span>
                  </div>
                ) : (
                  <div style={styles.itensLista}>
                    {itensSelecionados.map(item => (
                      <CardItemSelecionado
                        key={item.itemId}
                        item={item}
                        tipoOrdem={tipoOrdem}
                        onRemover={removerItem}
                        onAbrirVeiculo={abrirModalVeiculo}
                        onTogglePassivo={togglePassivo}
                        onAlterarValorPassivo={alterarValorPassivo}
                        onAlterarQuantidade={alterarQuantidadeItem}
                        subtotal={calcularSubtotalItem(item)}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Disponíveis */}
              <div style={styles.painel}>
                <h3 style={styles.painelTitulo}>Produtos Disponíveis</h3>
                <span style={styles.hintDuploClique}>duplo clique para adicionar</span>
                <div style={styles.buscaBox}>
                  <FaSearch style={styles.buscaIcone} />
                  <input
                    type="text"
                    value={buscaProduto}
                    onChange={(e) => setBuscaProduto(e.target.value)}
                    placeholder="Buscar serviço..."
                    style={styles.buscaInput}
                  />
                </div>
                <div style={styles.produtosLista}>
                  {servicosFiltrados.length > 0 ? (
                    servicosFiltrados.map(s => (
                      <div
                        key={s.idServico}
                        style={styles.produtoItem}
                        onDoubleClick={() => adicionarItemServico(s)}
                        title="Duplo clique para adicionar"
                      >
                        <span style={styles.produtoDescricao}>{s.descricao}</span>
                        <span style={styles.produtoValor}>R$ {formatarMoeda(s.valor)}</span>
                      </div>
                    ))
                  ) : (
                    <p style={styles.noResults}>Nenhum serviço encontrado</p>
                  )}
                </div>
              </div>
            </div>

            {/* Observações + Total + Ações */}
            <div style={styles.painel}>
              <label style={styles.label}>Observações</label>
              <textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                placeholder="Informações adicionais"
                rows="3"
                style={styles.textarea}
              />

              <div style={styles.rodape}>
                <div style={styles.totalBox}>
                  <FaMoneyBillWave style={{ marginRight: 8 }} />
                  VALOR TOTAL: <strong style={{ marginLeft: 8 }}>R$ {formatarMoeda(valorTotal)}</strong>
                </div>
                <div style={styles.botoesAcao}>
                  <button onClick={limparOrdem} style={styles.tealOutlineButton} disabled={loading}>
                    <FaArrowLeft /> Limpar
                  </button>
                  <button onClick={salvarOrdemServico} style={styles.tealButton} disabled={loading}>
                    {loading ? 'Salvando...' : (<><FaSave /> Salvar Ordem de Serviço</>)}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ══════════ MODAL: BUSCAR CLIENTE ══════════ */}
      {modalClienteAberto && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}><FaSearch style={{ marginRight: 8 }} /> Buscar Cliente</h2>
              <button onClick={() => setModalClienteAberto(false)} style={styles.modalClose}>&times;</button>
            </div>
            <div style={styles.modalContent}>
              <input
                type="text"
                value={termoPesquisaCliente}
                onChange={(e) => setTermoPesquisaCliente(e.target.value)}
                placeholder="Nome, CPF ou CNPJ..."
                style={styles.searchInputFull}
                autoFocus
              />

              {buscandoCliente ? (
                <div style={styles.loading}>Buscando...</div>
              ) : termoPesquisaCliente.trim().length < 3 ? (
                <div style={styles.noResults}>Digite pelo menos 3 caracteres</div>
              ) : resultadosPesquisaCliente.length > 0 ? (
                <div style={styles.resultadosClienteLista}>
                  {resultadosPesquisaCliente.map((cliente, idx) => (
                    <div key={idx} style={styles.resultadoClienteItem}>
                      <div style={styles.clienteAvatar}>{cliente.nome?.charAt(0) || '?'}</div>
                      <div style={{ flex: 1 }}>
                        <div style={styles.clienteNome}>{cliente.nome}</div>
                        <div style={styles.clienteSub}>{cliente.cpfCnpj}</div>
                      </div>
                      <button onClick={() => editarClienteEncontrado(cliente)} style={styles.iconButtonEdit} title="Editar cliente">
                        <FaEdit />
                      </button>
                      <button onClick={() => selecionarCliente(cliente)} style={styles.tealButton}>
                        <FaCheck /> Selecionar
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.semResultadoCliente}>
                  <span>Nenhum cliente encontrado</span>
                  <button onClick={solicitarCadastroCliente} style={styles.tealButton}>
                    <FaPlus /> Cadastrar novo cliente
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════ MODAL: TIPO DE ORDEM ══════════ */}
      {modalTipoOrdemAberto && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalPequeno}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Este atendimento é relacionado a veículo?</h2>
              {tipoOrdem && (
                <button onClick={() => setModalTipoOrdemAberto(false)} style={styles.modalClose}>&times;</button>
              )}
            </div>
            <div style={styles.modalContent}>
              <div style={styles.escolhasTipo}>
                <button onClick={() => escolherTipoOrdem('VEICULAR')} style={styles.escolhaTipoCard}>
                  <FaCar style={styles.escolhaTipoIcone} />
                  <span>Sim, relacionado a veículo</span>
                  <span style={styles.escolhaTipoHint}>Placa, RENAVAM, taxas de repasse</span>
                </button>
                <button onClick={() => escolherTipoOrdem('NAO_VEICULAR')} style={styles.escolhaTipoCard}>
                  <FaFileAlt style={styles.escolhaTipoIcone} />
                  <span>Não, outro tipo de serviço</span>
                  <span style={styles.escolhaTipoHint}>ITR, procuração, modelo 15, etc.</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ MODAL: VINCULAR VEÍCULO AO ITEM ══════════ */}
      {modalVeiculoItem.open && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}><FaCar style={{ marginRight: 8 }} /> Vincular Veículo</h2>
              <button onClick={() => setModalVeiculoItem({ open: false, itemId: null })} style={styles.modalClose}>&times;</button>
            </div>
            <div style={styles.modalContent}>
              <div style={styles.vinculoModoToggle}>
                <button
                  onClick={() => setModoVinculoVeiculo('buscar')}
                  style={modoVinculoVeiculo === 'buscar' ? styles.vinculoModoBotaoAtivo : styles.vinculoModoBotao}
                >
                  Buscar cadastrado
                </button>
                <button
                  onClick={() => setModoVinculoVeiculo('manual')}
                  style={modoVinculoVeiculo === 'manual' ? styles.vinculoModoBotaoAtivo : styles.vinculoModoBotao}
                >
                  Informar manualmente
                </button>
              </div>

              {modoVinculoVeiculo === 'buscar' ? (
                <>
                  <div style={styles.searchBox}>
                    <InputMask
                      value={placaPesquisaItem}
                      maxLength={8}
                      onChange={handlePlacaChangeItem}
                      placeholder="Caso não esteja listado, digite a placa"
                      style={styles.searchInput}
                      onKeyPress={(e) => e.key === 'Enter' && pesquisarPlacaItem()}
                      disabled={loadingPlaca}
                    />
                    <button
                      onClick={pesquisarPlacaItem}
                      style={styles.tealButton}
                      disabled={loadingPlaca || !placaPesquisaItem.trim()}
                    >
                      {loadingPlaca ? 'Pesquisando...' : 'Pesquisar Placa'}
                    </button>
                  </div>

                  {/* ── NOVO: atalho para cadastrar veículo direto, sem sair desta tela */}
                  <button
                    onClick={() => abrirCadastroVeiculo(modalVeiculoItem.itemId, placaPesquisaItem)}
                    style={styles.cadastrarVeiculoAtalho}
                  >
                    <FaPlus /> Cadastrar Veículo para {clienteSelecionado?.nome}
                  </button>

                  {clienteSelecionado?.veiculos?.length > 0 ? (
                    <>
                      <h3 style={{ margin: '20px 0 10px', fontSize: '15px', color: '#134e4a' }}>Veículos cadastrados</h3>
                      <div style={styles.veiculosGrid}>
                        {clienteSelecionado.veiculos.map(v => (
                          <div key={v.idVeiculo} style={styles.veiculoCard}>
                            <div style={styles.veiculoCardHeader}>
                              <FaCar style={styles.veiculoCardIcon} />
                              <div>
                                <h4 style={styles.veiculoCardModelo}>{v.marcaModelo}</h4>
                                <span style={styles.veiculoCardPlaca}>{v.placa}</span>
                              </div>
                            </div>
                            <div style={styles.veiculoCardDetails}>
                              <div><span style={styles.detailLabel}>Cor:</span> {v.cor}</div>
                              <div><span style={styles.detailLabel}>Ano:</span> {v.ano}</div>
                              <div><span style={styles.detailLabel}>RENAVAM:</span> {v.renavam}</div>
                            </div>
                            <button onClick={() => vincularVeiculoItem(modalVeiculoItem.itemId, v)} style={styles.tealButton}>
                              <FaCheck /> Selecionar
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p style={styles.noResults}>Este cliente ainda não possui veículos cadastrados</p>
                  )}
                </>
              ) : (
                <div style={styles.vinculoManualForm}>
                  <p style={styles.vinculoManualAviso}>
                    Use quando o veículo ainda não está cadastrado no nome do cliente (ex: emissão de ATPV-e). Preencha a placa ou o chassi.
                  </p>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Placa</label>
                    <InputMask
                      value={placaManualInput}
                      maxLength={8}
                      onChange={(e) => setPlacaManualInput(normalizePlaca(e.target.value))}
                      placeholder="AAA-0000"
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Chassi</label>
                    <input
                      type="text"
                      value={chassiManualInput}
                      onChange={(e) => setChassiManualInput(e.target.value.toUpperCase())}
                      placeholder="Número do chassi"
                      style={styles.input}
                      maxLength={17}
                    />
                  </div>
                  <button
                    onClick={() => vincularIdentificacaoManual(modalVeiculoItem.itemId)}
                    style={styles.tealButton}
                    disabled={!placaManualInput.trim() && !chassiManualInput.trim()}
                  >
                    <FaCheck /> Confirmar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════ MODAL: CADASTRAR NOVO VEÍCULO ══════════ */}
      {modalCadastroVeiculo.open && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Cadastrar Novo Veículo</h2>
              <button onClick={() => setModalCadastroVeiculo({ open: false, itemId: null })} style={styles.modalClose}>&times;</button>
            </div>
            <div style={styles.modalContent}>
              <ImportarDocumento
                tipo="veiculo"
                importacao={importacaoNovoVeiculo}
                onImportacaoChange={setImportacaoNovoVeiculo}
                onDadosLidos={aplicarDadosVeiculoImportado}
                campos={['placa', 'renavam', 'marcaModelo', 'cor', 'ano']}
              />
              <div style={styles.inputGroup}>
                <label style={styles.label}>Placa</label>
                <InputMask
                  value={novoVeiculo.placa}
                  maxLength={8}
                  onChange={(e) => setNovoVeiculo({ ...novoVeiculo, placa: normalizePlaca(e.target.value) })}
                  placeholder="AAA-0000"
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Marca/Modelo</label>
                <input
                  type="text"
                  value={novoVeiculo.marcaModelo}
                  onChange={(e) => setNovoVeiculo({ ...novoVeiculo, marcaModelo: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Cor</label>
                <input
                  type="text"
                  value={novoVeiculo.cor}
                  onChange={(e) => setNovoVeiculo({ ...novoVeiculo, cor: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Ano</label>
                <input
                  type="text"
                  value={novoVeiculo.ano}
                  onChange={(e) => setNovoVeiculo({ ...novoVeiculo, ano: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>RENAVAM</label>
                <input
                  type="text"
                  value={novoVeiculo.renavam}
                  onChange={(e) => setNovoVeiculo({ ...novoVeiculo, renavam: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.modalFooter}>
                <button onClick={() => setModalCadastroVeiculo({ open: false, itemId: null })} style={styles.tealOutlineButton}>
                  <FaTimes /> Cancelar
                </button>
                <button
                  onClick={cadastrarNovoVeiculo}
                  style={styles.tealButton}
                  disabled={!novoVeiculo.placa || !novoVeiculo.marcaModelo}
                >
                  <FaSave /> Cadastrar Veículo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {processoDaOrdem && (
        <ModalProcessoDaOrdem
          ordem={processoDaOrdem.ordem}
          quantidadeVeiculos={processoDaOrdem.quantidadeVeiculos}
          onAbrir={(processos) => navigate(processos.length ? `/processos?abrir=${processos[0].idProcesso}` : '/processos')}
          onPular={() => navigate('/ordemServico')}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// CARD DE ITEM SELECIONADO
// ══════════════════════════════════════════════════════════════
const CardItemSelecionado = ({ item, tipoOrdem, onRemover, onAbrirVeiculo, onTogglePassivo, onAlterarValorPassivo, onAlterarQuantidade, subtotal }) => {
  return (
    <div style={styles.itemCard}>
      <div style={styles.itemCardHeader}>
        <div style={styles.itemCardInfo}>
          {getServicoIcon(item.servico.descricao)}
          <span style={styles.itemCardDescricao}>{item.servico.descricao}</span>
        </div>
        <div style={styles.itemCardAcoes}>
          <span style={styles.itemCardValor}>R$ {formatarMoeda(item.servico.valor)}</span>
          <button onClick={() => onRemover(item.itemId)} style={styles.smallDeleteButton}>
            <FaTimes />
          </button>
        </div>
      </div>

      {tipoOrdem === 'NAO_VEICULAR' && (
        <div style={styles.quantidadeContainer}>
          <span style={styles.quantidadeLabel}>Quantidade:</span>
          <div style={styles.quantidadeStepper}>
            <button
              onClick={() => onAlterarQuantidade(item.itemId, -1)}
              style={styles.quantidadeBotao}
              disabled={(item.quantidade || 1) <= 1}
            >
              −
            </button>
            <span style={styles.quantidadeValor}>{item.quantidade || 1}</span>
            <button
              onClick={() => onAlterarQuantidade(item.itemId, 1)}
              style={styles.quantidadeBotao}
            >
              +
            </button>
          </div>
        </div>
      )}

      {tipoOrdem === 'VEICULAR' && (
        item.veiculo ? (
          <div style={styles.veiculoVinculadoTag}>
            <FaCar style={{ marginRight: 6 }} />
            <span>{item.veiculo.marcaModelo} — {item.veiculo.placa}</span>
            <button onClick={() => onAbrirVeiculo(item.itemId)} style={styles.trocarVeiculoLink}>trocar</button>
          </div>
        ) : (item.placaManual || item.chassiManual) ? (
          <div style={styles.veiculoVinculadoTag}>
            <FaCar style={{ marginRight: 6 }} />
            <span>
              {item.placaManual ? `Placa: ${item.placaManual}` : `Chassi: ${item.chassiManual}`}
              {' (sem cadastro)'}
            </span>
            <button onClick={() => onAbrirVeiculo(item.itemId)} style={styles.trocarVeiculoLink}>trocar</button>
          </div>
        ) : (
          <button onClick={() => onAbrirVeiculo(item.itemId)} style={styles.adicionarVeiculoButton}>
            <FaPlus /> Adicionar Veículo
          </button>
        )
      )}

      {item.passivos?.length > 0 && (
        <div style={styles.passivosItemContainer}>
          {item.passivos.map(p => (
            <div key={p.idServicoPassivo} style={styles.passivoItemLinha}>
              <input
                type="checkbox"
                checked={p.selecionado}
                onChange={(e) => onTogglePassivo(item.itemId, p.idServicoPassivo, e.target.checked)}
                style={styles.checkbox}
              />
              {getServicoPassivoIcon(p.descricao)}
              <span style={styles.passivoItemDescricao}>{p.descricao}</span>
              {p.valorDigitavel ? (
                <input
                  type="number"
                  value={p.selecionado ? (p.valor || '') : ''}
                  onChange={(e) => onAlterarValorPassivo(item.itemId, p.idServicoPassivo, parseFloat(e.target.value) || 0)}
                  style={styles.passivoItemInput}
                  placeholder="0,00"
                  step="0.01"
                  min="0"
                  disabled={!p.selecionado}
                />
              ) : (
                <span style={styles.passivoItemValor}>R$ {formatarMoeda(p.valor)}</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={styles.itemCardSubtotal}>
        <span>Subtotal:</span>
        <span style={styles.itemCardSubtotalValor}>R$ {formatarMoeda(subtotal)}</span>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// ESTILOS — paleta teal (Nexus CRVA)
// ══════════════════════════════════════════════════════════════
const TEAL = '#0f766e';
const TEAL_DARK = '#115e59';
const TEAL_LIGHT = '#f0fdfa';
const TEAL_BORDER = '#99f6e4';
const GREEN = '#16a34a';
const RED = '#dc2626';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    color: '#1f2937',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  content: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '20px'
  },
  titleIcon: {
    fontSize: '26px',
    color: TEAL
  },
  title: {
    fontSize: '22px',
    margin: 0,
    color: '#134e4a',
    fontWeight: '700'
  },
  subtitle: {
    margin: '2px 0 0 0',
    fontSize: '13px',
    color: '#6b7280'
  },

  painel: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '10px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    marginBottom: '20px'
  },
  painelTitulo: {
    fontSize: '16px',
    margin: '0 0 14px 0',
    color: '#134e4a',
    display: 'flex',
    alignItems: 'center',
    fontWeight: '700'
  },

  tealButton: {
    padding: '10px 16px',
    backgroundColor: TEAL,
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    justifyContent: 'center'
  },
  tealOutlineButton: {
    padding: '10px 16px',
    backgroundColor: 'white',
    color: TEAL,
    border: `1.5px solid ${TEAL}`,
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    justifyContent: 'center'
  },
  vinculoModoToggle: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    borderBottom: `1px solid ${TEAL_BORDER}`,
    paddingBottom: '12px'
  },
  vinculoModoBotao: {
    flex: 1,
    padding: '10px',
    backgroundColor: 'white',
    color: '#6b7280',
    border: '1px solid #ddd',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600'
  },
  vinculoModoBotaoAtivo: {
    flex: 1,
    padding: '10px',
    backgroundColor: TEAL,
    color: 'white',
    border: `1px solid ${TEAL}`,
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600'
  },
  vinculoManualForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  vinculoManualAviso: {
    fontSize: '13px',
    color: '#6b7280',
    marginBottom: '8px'
  },
  cadastrarVeiculoAtalho: {
    width: '100%',
    padding: '10px 14px',
    backgroundColor: TEAL_LIGHT,
    color: TEAL_DARK,
    border: `1px dashed ${TEAL}`,
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontSize: '13px',
    fontWeight: '600',
    marginBottom: '16px'
  },

  clienteBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: TEAL_LIGHT,
    borderRadius: '8px',
    border: `1px solid ${TEAL_BORDER}`
  },
  clienteAvatar: {
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    backgroundColor: TEAL,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '15px',
    flexShrink: 0
  },
  clienteNome: {
    fontWeight: '600',
    fontSize: '14px',
    color: '#134e4a'
  },
  clienteSub: {
    fontSize: '13px',
    color: '#6b7280',
    fontFamily: 'monospace',
    letterSpacing: '0.3px'
  },

  tipoOrdemBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: TEAL_LIGHT,
    border: `1px solid ${TEAL_BORDER}`,
    borderRadius: '8px',
    padding: '10px 16px',
    marginBottom: '20px'
  },
  tipoOrdemLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: '600',
    color: '#134e4a'
  },
  tipoOrdemAlterar: {
    background: 'none',
    border: 'none',
    color: TEAL,
    fontWeight: '600',
    fontSize: '13px',
    cursor: 'pointer',
    textDecoration: 'underline'
  },

  colunas: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    alignItems: 'start'
  },

  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    padding: '40px 10px',
    color: '#9ca3af',
    textAlign: 'center'
  },
  emptyStateIcon: {
    fontSize: '28px',
    marginBottom: '6px',
    color: '#d1d5db'
  },
  emptyStateHint: {
    fontSize: '12px'
  },

  itensLista: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxHeight: '560px',
    overflowY: 'auto'
  },

  itemCard: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '12px'
  },
  itemCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  itemCardInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: TEAL_DARK
  },
  itemCardDescricao: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1f2937'
  },
  itemCardAcoes: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  itemCardValor: {
    fontSize: '13px',
    color: GREEN,
    fontWeight: '600',
    fontFamily: 'monospace'
  },
  smallDeleteButton: {
    width: '26px',
    height: '26px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fee2e2',
    color: RED,
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '12px'
  },

  quantidadeContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '10px',
    padding: '8px 10px',
    backgroundColor: TEAL_LIGHT,
    borderRadius: '6px'
  },
  quantidadeLabel: {
    fontSize: '13px',
    color: '#134e4a',
    fontWeight: '600'
  },
  quantidadeStepper: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  quantidadeBotao: {
    width: '26px',
    height: '26px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${TEAL}`,
    borderRadius: '6px',
    backgroundColor: 'white',
    color: TEAL_DARK,
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '700',
    lineHeight: 1
  },
  quantidadeValor: {
    minWidth: '20px',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: '700',
    color: '#134e4a'
  },
  veiculoVinculadoTag: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '10px',
    padding: '8px 10px',
    backgroundColor: TEAL_LIGHT,
    borderRadius: '6px',
    fontSize: '13px',
    color: '#134e4a'
  },
  trocarVeiculoLink: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    color: TEAL,
    fontSize: '12px',
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  adicionarVeiculoButton: {
    marginTop: '10px',
    padding: '8px 12px',
    backgroundColor: TEAL_LIGHT,
    color: TEAL_DARK,
    border: `1px dashed ${TEAL}`,
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: '600',
    width: '100%',
    justifyContent: 'center'
  },

  passivosItemContainer: {
    marginTop: '10px',
    paddingTop: '10px',
    borderTop: '1px dashed #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  passivoItemLinha: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  passivoItemDescricao: {
    flex: 1,
    fontSize: '13px',
    color: '#4b5563'
  },
  passivoItemValor: {
    fontSize: '13px',
    color: '#b45309',
    fontFamily: 'monospace',
    minWidth: '70px',
    textAlign: 'right'
  },
  passivoItemInput: {
    width: '90px',
    padding: '4px 8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '13px',
    textAlign: 'right',
    fontFamily: 'monospace'
  },
  checkbox: {
    cursor: 'pointer'
  },

  itemCardSubtotal: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '10px',
    paddingTop: '8px',
    borderTop: '1px solid #e5e7eb',
    fontSize: '13px',
    fontWeight: '600',
    color: '#1f2937'
  },
  itemCardSubtotalValor: {
    color: GREEN,
    fontFamily: 'monospace'
  },

  hintDuploClique: {
    display: 'block',
    fontSize: '11px',
    color: '#9ca3af',
    fontStyle: 'italic',
    marginTop: '-10px',
    marginBottom: '12px'
  },

  buscaBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    marginBottom: '12px',
    backgroundColor: '#fafafa'
  },
  buscaIcone: {
    color: '#9ca3af',
    fontSize: '13px'
  },
  buscaInput: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    flex: 1,
    fontSize: '14px'
  },

  produtosLista: {
    maxHeight: '520px',
    overflowY: 'auto'
  },
  produtoItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 8px',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    userSelect: 'none'
  },
  produtoDescricao: {
    fontSize: '13px',
    color: TEAL_DARK,
    fontWeight: '500'
  },
  produtoValor: {
    fontSize: '13px',
    color: GREEN,
    fontFamily: 'monospace'
  },

  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    fontSize: '14px',
    color: '#374151'
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    resize: 'vertical'
  },

  rodape: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid #eee',
    flexWrap: 'wrap',
    gap: '15px'
  },
  totalBox: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '16px',
    fontWeight: '600',
    color: '#134e4a',
    backgroundColor: TEAL_LIGHT,
    padding: '10px 16px',
    borderRadius: '8px'
  },
  botoesAcao: {
    display: 'flex',
    gap: '10px'
  },

  inputGroup: {
    marginBottom: '14px'
  },
  input: {
    width: '95%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px'
  },

  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '10px',
    width: '90%',
    maxWidth: '820px',
    maxHeight: '85vh',
    overflowY: 'auto',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
  },
  modalPequeno: {
    backgroundColor: 'white',
    borderRadius: '10px',
    width: '90%',
    maxWidth: '520px',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '18px 20px',
    borderBottom: '1px solid #eee'
  },
  modalTitle: {
    fontSize: '17px',
    margin: 0,
    color: '#134e4a',
    display: 'flex',
    alignItems: 'center'
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#9ca3af'
  },
  modalContent: {
    padding: '20px'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '10px'
  },

  searchInputFull: {
    width: '95%',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    marginBottom: '16px'
  },
  searchBox: {
    display: 'flex',
    gap: '10px',
    marginBottom: '16px'
  },
  searchInput: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px'
  },

  resultadosClienteLista: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    maxHeight: '400px',
    overflowY: 'auto'
  },
  resultadoClienteItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px',
    border: '1px solid #eee',
    borderRadius: '8px'
  },
  iconButtonEdit: {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff7ed',
    color: '#c2410c',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  semResultadoCliente: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '14px',
    padding: '30px 0',
    color: '#6b7280'
  },

  escolhasTipo: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px'
  },
  escolhaTipoCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '24px 14px',
    border: `1.5px solid ${TEAL_BORDER}`,
    borderRadius: '10px',
    backgroundColor: TEAL_LIGHT,
    cursor: 'pointer',
    color: '#134e4a',
    fontSize: '14px',
    fontWeight: '600',
    textAlign: 'center'
  },
  escolhaTipoIcone: {
    fontSize: '28px',
    color: TEAL,
    marginBottom: '4px'
  },
  escolhaTipoHint: {
    fontSize: '11px',
    fontWeight: '400',
    color: '#6b7280'
  },

  veiculosGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
    gap: '14px'
  },
  veiculoCard: {
    border: '1px solid #ddd',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  veiculoCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    backgroundColor: TEAL_LIGHT
  },
  veiculoCardIcon: {
    fontSize: '18px',
    color: TEAL
  },
  veiculoCardModelo: {
    margin: '0 0 2px 0',
    fontSize: '13px',
    fontWeight: '600',
    color: '#134e4a'
  },
  veiculoCardPlaca: {
    fontSize: '12px',
    color: '#6b7280'
  },
  veiculoCardDetails: {
    padding: '10px 12px',
    fontSize: '12px',
    color: '#4b5563',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  detailLabel: {
    color: '#9ca3af',
    fontWeight: '500'
  },

  loading: {
    padding: '20px',
    textAlign: 'center',
    color: '#6b7280'
  },
  noResults: {
    padding: '20px',
    textAlign: 'center',
    color: '#9ca3af'
  }
};

export default OrdemServico;
